import React from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRightIcon,
  EnvelopeIcon,
  GlobeAltIcon,
  PhoneIcon,
} from '@heroicons/react/24/outline';

const TRUELOG_BLUE = '#385CF2';
const TRUELOG_CYAN = '#0E9ED5';

const footerLinks = {
  Services: [
    { name: 'Trade Compliance', href: '/services/cross-border-compliance' },
    { name: 'Global Transport & Freight', href: '/services/global-transport-freight' },
    { name: 'Warehousing & Fulfilment', href: '/services/warehousing-fulfilment' },
  ],
  Company: [
    { name: 'About Us', href: '/about-us' },
    { name: 'Why TrueLog', href: '/why-truelog' },
    { name: 'Global Coverage', href: '/global-coverage' },
    { name: 'Contact Us', href: '/contact-us' },
  ],
  Resources: [
    { name: 'Blog', href: '/blog' },
    { name: 'Case Studies', href: '/case-studies' },
    { name: 'FAQ', href: '/faq' },
  ],
  Legal: [{ name: 'Privacy Policy', href: '/privacy-policy' }],
};

const Footer: React.FC = () => {
  return (
    <footer className="site-footer bg-slate-950 text-white">
      <div className="mx-auto max-w-7xl px-4 pt-10 pb-10 sm:px-6 lg:px-8">
        {/* CTA Banner */}
        <div className="mb-10 rounded-3xl border border-white/10 bg-white/[0.05] p-8 lg:flex lg:items-center lg:justify-between lg:gap-8">
          <div>
            <h2 className="text-2xl font-bold text-white sm:text-3xl">
              Build your global operations on ICT logistics that scale.
            </h2>
            <p className="mt-3 max-w-2xl text-sm leading-7 text-slate-400">
              Partner with TrueLog to move ICT infrastructure faster across markets, without operational friction.
            </p>
          </div>
          <div className="mt-6 flex flex-shrink-0 gap-3 lg:mt-0">
            <Link
              to="/contact-us"
              className="inline-flex items-center rounded-2xl px-6 py-3 text-sm font-bold uppercase tracking-wider text-white transition-colors hover:opacity-90"
              style={{ background: `linear-gradient(135deg, ${TRUELOG_CYAN}, ${TRUELOG_BLUE})` }}
            >
              Get a Quote
              <ArrowRightIcon className="ml-2 h-4 w-4" />
            </Link>
            <Link
              to="/services"
              className="inline-flex items-center rounded-2xl border border-white/15 px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-white/10"
            >
              Explore our Solutions
            </Link>
          </div>
        </div>

        {/* Footer Grid */}
        <div className="grid grid-cols-2 gap-8 lg:grid-cols-[1.2fr_repeat(4,1fr)]">
          {/* Brand Column */}
          <div className="col-span-2 lg:col-span-1">
            <Link to="/" className="inline-block">
              <img
                src={`${process.env.PUBLIC_URL}/assets/images/truelog-logo-horizontal.png`}
                alt="TrueLog Logo"
                className="h-14 w-auto"
              />
            </Link>

            <p className="mt-5 max-w-xs text-sm leading-7 text-slate-400">
              Integrated ICT logistics — from import compliance to deployment, returns, and lifecycle control.
            </p>

            <div className="mt-6 space-y-2.5 text-sm text-slate-400">
              <a href="mailto:sales@truelog.com.sg" className="flex items-center gap-2 hover:text-white transition-colors">
                <EnvelopeIcon className="h-4 w-4 text-cyan-400" />
                sales@truelog.com.sg
              </a>
              <a href="tel:+6569093756" className="flex items-center gap-2 hover:text-white transition-colors">
                <PhoneIcon className="h-4 w-4 text-cyan-400" />
                +65 6909 3756
              </a>
              <span className="flex items-center gap-2">
                <GlobeAltIcon className="h-4 w-4 text-cyan-400" />
                Singapore, Antwerp, Chennai, Colombo, Dubai, Ho Chi Minh City
              </span>
            </div>
          </div>

          {/* Link Columns */}
          {Object.entries(footerLinks).map(([category, links]) => (
            <div key={category}>
              <h3 className="text-sm font-semibold uppercase tracking-wider text-white">{category}</h3>
              <ul className="mt-4 space-y-2.5">
                {links.map((link) => (
                  <li key={link.name}>
                    <Link
                      to={link.href}
                      className="text-sm text-slate-400 transition-colors hover:text-white"
                    >
                      {link.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Bottom Bar */}
        <div className="mt-12 flex flex-col gap-4 border-t border-white/10 pt-8 md:flex-row md:items-center md:justify-between">
          <div className="flex items-center gap-4 text-sm text-slate-500">
            <span>IATA Certified since 2019</span>
            <span>FIATA Member</span>
          </div>
          <p className="text-sm text-slate-500">&copy; {new Date().getFullYear()} TrueLog. All rights reserved.</p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
